import { useState } from "react";
import { Auth } from "aws-amplify";
import { toast } from "react-toastify";
import { TbSearch, TbFileText, TbDownload, TbEye, TbSend } from "react-icons/tb";
import appConfig from "../../../../config/app-config";
import PageHeader from "../../../molecules/PageHeader";
import IconRegistrationProfile from "../../../atoms/IconRegistrationProfile";
import FilePreview from "../../../atoms/FilePreview";
import SignatoryVerificationModal, { VerificationRequestData } from "./SignatoryVerificationModal";

const { address: PERSON_API_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;

const REPRESENTATION_FORMAT_MAP: Record<string, string> = {
    'ISOLATED_OR_JOINT': 'Isolada ou Em Conjunto',
    'ISOLATED': 'Isoladamente',
    'ISOLATED_AND_OR_JOINT': 'Isolada e/ou em conjunto',
    'JOINT_TWO_DIRECTORS': 'Conjunto de 2 signatários',
    'DEPENDS_OBLIGATION': 'Depende Obrig/Ope',
    'JOINT': 'Em conjunto',
    'ISOLATED_ATTORNEY': 'Isolad. Procurador',
    'JOINT_TWO_ATTORNEYS': 'Conjunto de 2 Procuradores',
    'CONDITIONAL_VALUE_OBLIGATION': 'Condicionada por valor',
    'TWO_PERSONS': '2 Pessoas',
    'THREE_PERSONS': '3 Pessoas'
};

interface LegalPersonResult {
    personId: string;
    legalName: string;
    fantasyName: string;
    govTaxIdFormatted: string;
    status: string;
    profiles: string[];
}

interface CompanyBankAccount {
    bank: string;
    agency: string;
    account: string;
    active: boolean;
    isBranch?: boolean;
    branchGovTaxId?: string;
    branchLegalName?: string;
}

interface CompanyDetail {
    registrationStatus: string;
    zipcode: string;
    street: string;
    streetType: string;
    number: string;
    complement: string;
    district: string;
    bankAccounts: CompanyBankAccount[];
}

const REGISTRATION_STATUS_MAP: Record<string, { label: string; className: string }> = {
    'IN_NEGOTIATION': { label: 'Em Tratativa', className: 'bg-yellow-500 text-white' },
    'WAITING_PARTNER': { label: 'Aguardando Parceira', className: 'bg-blue-500 text-white' },
    'BLOCKED': { label: 'Bloqueada', className: 'bg-red-500 text-white' },
    'COMPLETED': { label: 'Concluída', className: 'bg-green-500 text-white' },
};

interface SignatoryFromApi {
    id: number;
    individualUuid: string;
    fullName: string;
    socialId: string;
    email: string;
    mobile: string;
    position: string;
    allowedOperations: Array<{ operation: string; representationFormat: string } | string>;
    proxyExpirationDate?: string;
    proxyAllowedValue?: number;
    signAsEntries: { signAs: string; useDigitalCertificate: boolean }[];
    isActive: boolean;
}

interface DocumentFromApi {
    id: string;
    type: string;
    number: string;
    issuingAuthority?: string;
    fileUuid?: string;
    fileName?: string;
}

const SignatoryListPage = () => {
    const [searchCnpj, setSearchCnpj] = useState("");
    const [searchName, setSearchName] = useState("");
    const [legalPersons, setLegalPersons] = useState<LegalPersonResult[]>([]);
    const [selectedPerson, setSelectedPerson] = useState<LegalPersonResult | null>(null);
    const [signatories, setSignatories] = useState<SignatoryFromApi[]>([]);
    const [companyDetail, setCompanyDetail] = useState<CompanyDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingSignatories, setLoadingSignatories] = useState(false);
    const [companyDocs, setCompanyDocs] = useState<DocumentFromApi[]>([]);
    const [signatoryDocs, setSignatoryDocs] = useState<Record<string, DocumentFromApi[]>>({});
    const [previewDoc, setPreviewDoc] = useState<{ url: string; fileName: string } | null>(null);
    const [verificationModalOpen, setVerificationModalOpen] = useState(false);

    const getHeaders = async () => {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        return {
            'x-api-key': PERSON_API_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    };

    const searchLegalPersons = async () => {
        if (!searchCnpj.trim() && !searchName.trim()) return;
        setLoading(true);
        setSelectedPerson(null);
        setSignatories([]);
        try {
            const headers = await getHeaders();
            const res = await fetch(`${PERSON_API_URL}/legal-persons/page-list?govTaxId=${encodeURIComponent(searchCnpj)}&legalName=${encodeURIComponent(searchName)}&page=0&size=20&sort=id,DESC`, { headers });
            if (res.ok) {
                const data = await res.json();
                setLegalPersons(data.list || []);
            } else {
                setLegalPersons([]);
            }
        } catch (e) {
            console.error('Erro ao buscar PJs:', e);
            setLegalPersons([]);
        } finally {
            setLoading(false);
        }
    };

    const selectLegalPerson = async (person: LegalPersonResult) => {
        setSelectedPerson(person);
        setSignatories([]);
        setCompanyDetail(null);
        setCompanyDocs([]);
        setSignatoryDocs({});
        setLoadingSignatories(true);
        try {
            const headers = await getHeaders();
            const [sigRes, detailRes, docsRes] = await Promise.all([
                fetch(`${PERSON_API_URL}/legal-persons/${person.personId}/signatories`, { headers }),
                fetch(`${PERSON_API_URL}/legal-persons/${person.personId}/page-form`, { headers }),
                fetch(`${PERSON_API_URL}/person/${person.personId}/documents`, { headers }),
            ]);
            let activeSigs: SignatoryFromApi[] = [];
            if (sigRes.ok) {
                const data = await sigRes.json();
                activeSigs = (data.signatories || []).filter((s: SignatoryFromApi) => s.isActive);
                setSignatories(activeSigs);
            }
            if (detailRes.ok) {
                const detail = await detailRes.json();
                setCompanyDetail(detail);
            }
            if (docsRes.ok) {
                const docsData = await docsRes.json();
                setCompanyDocs(docsData.documents || []);
            }
            // Fetch documents for each signatory
            const sigDocMap: Record<string, DocumentFromApi[]> = {};
            await Promise.all(activeSigs.map(async (sig) => {
                try {
                    const res = await fetch(`${PERSON_API_URL}/person/${sig.individualUuid}/documents`, { headers });
                    if (res.ok) {
                        const data = await res.json();
                        sigDocMap[sig.individualUuid] = data.documents || [];
                    }
                } catch { /* ignore */ }
            }));
            setSignatoryDocs(sigDocMap);
        } catch (e) {
            console.error('Erro ao buscar dados:', e);
        } finally {
            setLoadingSignatories(false);
        }
    };

    const handleKeyUp = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') searchLegalPersons();
    };

    const handleDocDownload = async (personUuid: string, doc: DocumentFromApi) => {
        if (!doc.fileUuid) return;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${PERSON_API_URL}/person/${personUuid}/documents/${doc.id}/download-url`, { headers });
            if (res.ok) { const data = await res.json(); window.open(data.downloadUrl, '_blank'); }
        } catch { /* ignore */ }
    };

    const handleDocPreview = async (personUuid: string, doc: DocumentFromApi) => {
        if (!doc.fileUuid) return;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${PERSON_API_URL}/person/${personUuid}/documents/${doc.id}/download-url`, { headers });
            if (res.ok) { const data = await res.json(); setPreviewDoc({ url: data.downloadUrl, fileName: data.fileName || doc.fileName || 'file' }); }
        } catch { /* ignore */ }
    };

    const DocumentList: React.FC<{ docs: DocumentFromApi[]; personUuid: string; title: string }> = ({ docs, personUuid, title }) => {
        if (docs.length === 0) return null;
        return (
            <div className="mt-3 pt-3 border-t border-gray-100">
                <span className="block text-xs text-gray-400 uppercase mb-1">{title}</span>
                <div className="flex flex-wrap gap-2">
                    {docs.map((doc) => (
                        <div key={doc.id} className="flex items-center gap-1 text-xs px-2 py-1 bg-gray-50 border border-gray-200 rounded">
                            <TbFileText size={14} className="text-blue-600" />
                            <span>{doc.type} — {doc.number}</span>
                            {doc.fileUuid && (
                                <>
                                    <button onClick={() => handleDocPreview(personUuid, doc)} className="text-blue-600 hover:text-blue-800 ml-1" title="Visualizar"><TbEye size={14} /></button>
                                    <button onClick={() => handleDocDownload(personUuid, doc)} className="text-green-600 hover:text-green-800" title="Download"><TbDownload size={14} /></button>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full">
            <PageHeader
                title="Signatários"
                breadcrumb={[{ label: 'Signatários', url: '#' }]}
                apiVersion=""
            >
                {null}
            </PageHeader>

            <div className="p-4">
                {/* Busca */}
                <div className="flex gap-2 mb-4">
                    <div className="relative flex-1 max-w-xs">
                        <input
                            id="src-signatory-cnpj"
                            type="text"
                            className="block py-2.5 px-2 w-full text-sm text-gray-900 bg-white border border-gray-200 focus:outline-none focus:ring-0 focus:border-blue-600 peer appearance-none"
                            placeholder=" "
                            value={searchCnpj}
                            onChange={(e) => setSearchCnpj(e.target.value)}
                            onKeyUp={handleKeyUp}
                        />
                        <label htmlFor="src-signatory-cnpj"
                            className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 start-1">
                            Buscar por CNPJ...
                        </label>
                    </div>
                    <div className="relative flex-1 max-w-md">
                        <input
                            id="src-signatory-name"
                            type="text"
                            className="block py-2.5 px-2 w-full text-sm text-gray-900 bg-white border border-gray-200 focus:outline-none focus:ring-0 focus:border-blue-600 peer appearance-none"
                            placeholder=" "
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            onKeyUp={handleKeyUp}
                        />
                        <label htmlFor="src-signatory-name"
                            className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 start-1">
                            Buscar por razão social...
                        </label>
                    </div>
                    <button
                        onClick={searchLegalPersons}
                        disabled={loading}
                        className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
                    >
                        <TbSearch size={18} />
                        Buscar
                    </button>
                    <button
                        onClick={() => { setSearchCnpj(""); setSearchName(""); setLegalPersons([]); setSelectedPerson(null); setSignatories([]); setCompanyDetail(null); setCompanyDocs([]); setSignatoryDocs({}); }}
                        disabled={loading || loadingSignatories}
                        className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 transition-colors disabled:opacity-50"
                    >
                        Limpar
                    </button>
                </div>

                {/* Lista de PJs encontradas */}
                {legalPersons.length > 0 && !selectedPerson && (
                    <div className="mb-4">
                        <h3 className="text-sm font-medium text-gray-600 mb-2">Selecione a empresa:</h3>
                        <div className="grid gap-2">
                            {legalPersons.map((lp) => (
                                <button
                                    key={lp.personId}
                                    onClick={() => selectLegalPerson(lp)}
                                    className="text-left p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                                >
                                    <span className="font-medium text-gray-800">{lp.legalName}</span>
                                    <span className="text-gray-500 ml-2">({lp.govTaxIdFormatted})</span>
                                    {lp.fantasyName && <span className="text-gray-400 ml-2">— {lp.fantasyName}</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {legalPersons.length === 0 && !loading && (searchCnpj || searchName) && !selectedPerson && (
                    <p className="text-gray-500 text-sm">Nenhuma empresa encontrada.</p>
                )}

                {loading && <p className="text-gray-500 text-sm">Buscando...</p>}

                {/* PJ selecionada + signatários */}
                {selectedPerson && (
                    <div>
                        <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800">{selectedPerson.legalName}</h3>
                                    {selectedPerson.fantasyName && (
                                        <p className="text-sm text-gray-500">{selectedPerson.fantasyName}</p>
                                    )}
                                </div>
                                <button
                                    onClick={() => { setSelectedPerson(null); setSignatories([]); setCompanyDetail(null); setCompanyDocs([]); setSignatoryDocs({}); }}
                                    className="text-sm text-blue-600 hover:underline"
                                >
                                    Selecionar outra empresa
                                </button>
                            </div>

                            <div className="grid grid-cols-3 gap-4 text-sm">
                                <div>
                                    <span className="block text-xs text-gray-400 uppercase">CNPJ</span>
                                    <span className="text-gray-800 font-medium">{selectedPerson.govTaxIdFormatted}</span>
                                </div>
                                {companyDetail && (
                                    <div>
                                        <span className="block text-xs text-gray-400 uppercase">Status de Cadastro</span>
                                        {(() => {
                                            const st = REGISTRATION_STATUS_MAP[companyDetail.registrationStatus] || { label: companyDetail.registrationStatus, className: 'bg-slate-500 text-white' };
                                            return <span className={`inline-block text-xs px-2 py-0.5 rounded ${st.className}`}>{st.label}</span>;
                                        })()}
                                    </div>
                                )}
                                {selectedPerson.profiles?.filter(Boolean).length > 0 && (
                                    <div>
                                        <span className="block text-xs text-gray-400 uppercase">Perfis</span>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            {selectedPerson.profiles.filter(Boolean).map((profile, i) => (
                                                <span key={i} className="mr-1">
                                                    <IconRegistrationProfile profile={profile} />
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {companyDetail && (
                                <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-gray-100 text-sm">
                                    {companyDetail.street && (
                                        <div>
                                            <span className="block text-xs text-gray-400 uppercase">Endereço</span>
                                            <span className="text-gray-700">
                                                {[companyDetail.streetType, companyDetail.street, companyDetail.number].filter(Boolean).join(' ')}
                                                {companyDetail.complement && `, ${companyDetail.complement}`}
                                            </span>
                                            <span className="block text-gray-500">
                                                {companyDetail.district}{companyDetail.zipcode && ` — CEP ${companyDetail.zipcode}`}
                                            </span>
                                        </div>
                                    )}
                                    {companyDetail.bankAccounts?.filter(b => b.active).length > 0 && (
                                        <div>
                                            <span className="block text-xs text-gray-400 uppercase">Conta Bancária</span>
                                            {companyDetail.bankAccounts.filter(b => b.active).map((b, i) => (
                                                <div key={i} className="text-gray-700">
                                                    <span>{b.bank} — Ag {b.agency} / Cc {b.account}</span>
                                                    {b.isBranch && (
                                                        <span className="ml-2 text-xs text-orange-700 bg-orange-50 px-1.5 py-0.5 rounded">
                                                            Filial: {b.branchGovTaxId?.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')} — {b.branchLegalName}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <DocumentList docs={companyDocs} personUuid={selectedPerson.personId} title="Documentos da Empresa" />

                            {/* Consultar Cadastro alert */}
                            {companyDetail && (companyDetail as any).requiresSignatoryVerification && (
                                <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-center justify-between">
                                    <div>
                                        <span className="text-sm font-semibold text-amber-800">⚠️ Consultar Cadastro</span>
                                        <p className="text-xs text-amber-600">O cadastro sinalizou que esta PJ requer verificação de signatários antes da confecção de contratos.</p>
                                    </div>
                                    <button onClick={() => setVerificationModalOpen(true)} className="flex items-center gap-1 bg-amber-500 text-white px-3 py-2 rounded text-sm hover:bg-amber-600 transition-colors whitespace-nowrap">
                                        <TbSend size={16} /> Abrir Solicitação
                                    </button>
                                </div>
                            )}
                        </div>

                        {loadingSignatories ? (
                            <p className="text-gray-500 text-sm">Carregando signatários...</p>
                        ) : signatories.length === 0 ? (
                            <p className="text-gray-500 text-sm">Nenhum signatário ativo encontrado para esta empresa.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left border border-gray-200">
                                    <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                                        <tr>
                                            <th className="px-4 py-3">Nome</th>
                                            <th className="px-4 py-3">CPF</th>
                                            <th className="px-4 py-3">Cargo</th>
                                            <th className="px-4 py-3">Perfil de Assinatura</th>
                                            <th className="px-4 py-3">Operações Permitidas</th>
                                            <th className="px-4 py-3">Venc. Procuração</th>
                                            <th className="px-4 py-3">Valor Permitido</th>
                                            <th className="px-4 py-3">Contato</th>
                                            <th className="px-4 py-3">Docs</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {signatories.map((sig, idx) => (
                                            <tr key={sig.id} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100`}>
                                                <td className="px-4 py-3 font-medium text-gray-800">{sig.fullName}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">{sig.socialId}</td>
                                                <td className="px-4 py-3">{sig.position}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-wrap gap-1">
                                                        {sig.signAsEntries.map((entry, i) => (
                                                            <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                                                {entry.signAs}
                                                                {entry.useDigitalCertificate && <span className="text-green-600" title="Certificado Digital">🔐</span>}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-wrap gap-1">
                                                        {sig.allowedOperations.map((op: any, i: number) => {
                                                            const opName = typeof op === 'object' ? op.operation : op;
                                                            const opFormat = typeof op === 'object' ? op.representationFormat : null;
                                                            return (
                                                                <span key={i} className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                                                    {opName}{opFormat ? ` — ${REPRESENTATION_FORMAT_MAP[opFormat] || opFormat}` : ''}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    {sig.proxyExpirationDate ? new Date(sig.proxyExpirationDate + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    {sig.proxyAllowedValue != null ? sig.proxyAllowedValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="text-xs">
                                                        {sig.email && <div>{sig.email}</div>}
                                                        {sig.mobile && <div>{sig.mobile}</div>}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {(signatoryDocs[sig.individualUuid] || []).filter(d => d.fileUuid).length > 0 ? (
                                                        <div className="flex flex-wrap gap-1">
                                                            {signatoryDocs[sig.individualUuid].filter(d => d.fileUuid).map((doc) => (
                                                                <div key={doc.id} className="flex items-center gap-0.5">
                                                                    <button onClick={() => handleDocPreview(sig.individualUuid, doc)} className="text-blue-600 hover:text-blue-800" title={`${doc.type} — Visualizar`}><TbEye size={14} /></button>
                                                                    <button onClick={() => handleDocDownload(sig.individualUuid, doc)} className="text-green-600 hover:text-green-800" title={`${doc.type} — Download`}><TbDownload size={14} /></button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {previewDoc && (
                <FilePreview url={previewDoc.url} fileName={previewDoc.fileName} onClose={() => setPreviewDoc(null)} />
            )}

            {selectedPerson && (
                <SignatoryVerificationModal
                    isOpen={verificationModalOpen}
                    onClose={() => setVerificationModalOpen(false)}
                    onSend={async (data: VerificationRequestData) => {
                        try {
                            const headers = await getHeaders();
                            const res = await fetch(`${PERSON_API_URL}/legal-persons/${selectedPerson.personId}/signatory-verification-request`, {
                                method: 'POST', headers, body: JSON.stringify({ operation: data.operation, operationType: data.operationType, value: data.value, nominees: data.nominees })
                            });
                            if (!res.ok) throw new Error('Erro ao enviar');
                            toast.success('Solicitação enviada com sucesso!');
                        } catch (e) {
                            toast.error('Erro ao enviar solicitação. Tente novamente.');
                            throw e;
                        }
                    }}
                />
            )}
        </div>
    );
};

export default SignatoryListPage;
